import { useState } from 'react'
import { formatMeasureValue } from '../lib/visualContract'
import { getMeasureMetadata } from '../lib/analyticalQuery'

/**
 * Table — displays every row from a QueryResult, one column per yAxis
 * measure. Sort is LOCAL UI state only (click a column header) — never
 * persisted as part of the VisualDefinition, per the approved Phase 5B
 * decision that sort order is a render-time concern, not a saved one.
 *
 * onRowClick (Phase 6C, optional): same contract as Bar.jsx's — called
 * with the clicked row's {key, label, values} object, nothing more. This
 * component has no knowledge of drill-down or interactionState.js.
 * Omitting it preserves the exact prior behavior (no row cursor/hover
 * change), so existing consumers are unaffected.
 */
export default function Table({ result, yAxis, onRowClick }) {
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('desc')

  const rows = result?.rows || []

  const sortedRows = sortKey
    ? [...rows].sort((a, b) => {
        const av = a.values[sortKey] ?? 0
        const bv = b.values[sortKey] ?? 0
        return sortDir === 'desc' ? bv - av : av - bv
      })
    : rows

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  if (rows.length === 0) {
    return <div style={{ padding: 24, textAlign: 'center', color: '#9CA3AF', fontSize: 12.5 }}>No data available</div>
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead>
        <tr style={{ background: '#F9FAFB' }}>
          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#6B7280' }}>Item</th>
          {yAxis.map(m => (
            <th
              key={m}
              onClick={() => handleSort(m)}
              style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#6B7280', cursor: 'pointer' }}
            >
              {getMeasureMetadata(m).label}{sortKey === m ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(row => (
          <tr
            key={row.key}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            style={{ borderTop: '1px solid #F3F4F6', cursor: onRowClick ? 'pointer' : 'default' }}
          >
            <td style={{ padding: '8px 12px', color: '#0F1017' }}>{row.label}</td>
            {yAxis.map(m => (
              <td key={m} style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                {formatMeasureValue(m, row.values[m])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
